import { ButtonLabel } from 'components/Button';
import { Icon } from 'components/Icon';
import moment from 'moment';
import cn from 'classnames';

import 'moment/locale/ru';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import MomentLocaleUtils from 'react-day-picker/moment';
import InputMask from 'react-input-mask';
import { useFirstRender } from 'hooks/useFirstRender';

export const InputRangeDatePicker = ({ handleFilter, close, boxShadow, relative }) => {
  const [{ from, to, showDatePicker }, setState] = useState({
    from: undefined,
    to: undefined,
    showDatePicker: false,
  });

  const firstRender = useFirstRender();
  const classNames = ['range-date'];

  const dayPickerRef = useRef(null);

  if (boxShadow) {
    classNames.push('box-shadow');
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dayPickerRef.current && !dayPickerRef.current.contains(event.target)) {
        setState((state) => {
          if (state.to && state.from) {
            return state;
          }
          return {
            to: undefined,
            from: undefined,
            showDatePicker: false,
          };
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dayPickerRef]);

  useEffect(() => {
    if (firstRender) return;
    if (from && to) {
      const fromTransformed = new Date(from);
      const toTransformed = new Date(to);
      fromTransformed.setHours(0, 0, 1);
      toTransformed.setHours(23, 59, 59);
      setState((state) => {
        return { ...state, showDatePicker: false };
      });
      handleFilter({ from: fromTransformed, to: toTransformed });
    } else if (!from && !to) {
      handleFilter();
    }
  }, [from, to]);

  const handleFromChange = ({ target: { value } }) => {
    const date = moment(value, 'DD.MM.YYYY', true);
    if (date.isValid()) {
      setState((state) => {
        return { ...state, from: date.toDate() };
      });
    }
  };

  const handleToChange = ({ target: { value } }) => {
    const date = moment(value, 'DD.MM.YYYY', true);
    if (date.isValid()) {
      setState((state) => {
        return { ...state, to: date.toDate() };
      });
    }
  };

  const handleDayClick = (day) => {
    if (from && to) {
      setState((state) => {
        return { ...state, from: day, to: undefined };
      });
    } else {
      const range = DateUtils.addDayToRange(day, { from, to });
      setState((state) => {
        return { ...state, from: range.from, to: range.to };
      });
    }
  };

  const handleBlur = () => {
    // setShowDatePicker(false);
  };

  const handleFocus = () => {
    setState((state) => {
      return { ...state, showDatePicker: true };
    });
  };

  const handleResetDates = () => {
    setState((state) => {
      return { ...state, from: undefined, to: undefined };
    });
    close();
  };

  const modifiers = { start: from, end: to };

  return (
    <div className={cn(classNames.join(' '), cn(relative && 'absolute-block'))} ref={dayPickerRef}>
      <div className="inputs-wrapper">
        <InputMask
          mask="99.99.9999"
          value={from ? moment(from).format('DD.MM.YYYY') : ''}
          onChange={handleFromChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
        >
          {(inputProps) => (
            <div className="input-wrapper">
              <input {...inputProps} type="text" placeholder="__.__.____" />
              <Icon name="calendar" />
            </div>
          )}
        </InputMask>
        <span className="delimiter">по</span>
        <InputMask
          mask="99.99.9999"
          value={to ? moment(to).format('DD.MM.YYYY') : ''}
          onChange={handleToChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
        >
          {(inputProps) => (
            <div className="input-wrapper">
              <input {...inputProps} type="text" placeholder="__.__.____" />
              <Icon name="calendar" />
            </div>
          )}
        </InputMask>
        {from && to && (
          <div className={cn(relative && 'relative-button')}>
            <ButtonLabel rounded={true} onClick={handleResetDates} text="Очистить фильтр" />
          </div>
        )}
      </div>

      {showDatePicker && (
        <DayPicker
          className={cn('Selectable', relative && 'Filled')}
          localeUtils={MomentLocaleUtils}
          locale="ru"
          disabledDays={{ before: from }}
          selectedDays={[from, { from, to }]}
          modifiers={modifiers}
          onDayClick={handleDayClick}
        />
      )}
    </div>
  );
};

InputRangeDatePicker.propTypes = {
  handleFilter: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  boxShadow: PropTypes.bool,
  relative: PropTypes.bool,
};

InputRangeDatePicker.defaultProps = {
  boxShadow: true,
  relative: false,
};
